import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Box, Typography, Button, Chip, Autocomplete, TextField } from '@mui/material';
import { useFormContext } from '../../contexts/FormContext';
import { useParams } from 'react-router-dom';
import { Server } from '../../services/Server';
import { useQuery } from '@tanstack/react-query';
import Chart from '../common/Chart';
import toast from 'react-hot-toast';
import { useAppContext } from '../../contexts/AppContext';
import { buildSoilingRatiosParams, buildSoilingRatiosBody } from '../../utils/utils';

interface SoilingRatioMultiSelectProps {
  field: any;
}

const SoilingRatioMultiSelect: React.FC<SoilingRatioMultiSelectProps> = () => {
  const { formData, setFormData, getFormDataItem } = useFormContext();
  const { role } = useAppContext();
  const { setModified } = useAppContext();
  const params = useParams();
  const { URL_BASE, postToServerWithParams } = Server();
  const [selectedCurves, setSelectedCurves] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [combinedChartData, setCombinedChartData] = useState<any>(null);
  const hasRestoredDataRef = useRef(false);

  // Helper function to get the correct soiling ratio operational key based on role
  const soilingRatioOperationalKey = role === 'ex_post_analysis' ? 'soiling_ratio_operational_ex_post' : 'soiling_ratio_operational';

  // Memoize sensor data to avoid re-renders - use JSON.stringify to create stable reference
  const sensorDataString = useMemo(() => {
    const soilingDataKeys = Object.keys(formData).filter((key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'));
    const data: { [key: string]: { file_uid: string; name: string } } = {};
    soilingDataKeys.forEach((blockKey) => {
      const fileUid = getFormDataItem(formData, `${blockKey}.file_uid`);
      const name = getFormDataItem(formData, `${blockKey}.name`);
      if (fileUid) {
        data[fileUid] = { file_uid: fileUid, name: name || '' };
      }
    });
    return JSON.stringify(data);
  }, [formData, getFormDataItem]);

  const sensorData = useMemo(() => JSON.parse(sensorDataString), [sensorDataString]);

  // Function to get sensor name from file_uid
  const getSensorName = useCallback(
    (fileUid: string) => {
      if (!fileUid) return '';
      const sensor = sensorData[fileUid];
      return sensor?.name || `Sensor ${fileUid.substring(0, 8)}...`;
    },
    [sensorData],
  );

  // Use shared utility functions to build params and body for consistent cache keys
  const requestParams = buildSoilingRatiosParams(formData, getFormDataItem, params.id, role);
  const chartBody = buildSoilingRatiosBody(formData, getFormDataItem, 'rain_pattern');

  // Fetch data from SMTChart cache (same cache key)
  const {
    data: soilingRatioData = [],
    isLoading: isDataLoading,
    isFetching: isDataFetching,
    isError: isDataError,
  } = useQuery({
    queryKey: [
      'smt-chart-' +
        JSON.stringify({
          ...requestParams,
          ...chartBody,
        }),
      'soiling-ratios',
    ],
    queryFn: () => postToServerWithParams(`${URL_BASE}soiling-ratios`, requestParams, chartBody),
    enabled: !!(
      formData?.location?.latitude &&
      formData?.location?.longitude &&
      !isNaN(Number(formData.location.latitude)) &&
      !isNaN(Number(formData.location.longitude))
    ),
    retry: 0,
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    staleTime: 0, // Consider data as stale immediately
  });

  // Calculate available curves directly (without useEffect to avoid loops)
  const availableCurves = useMemo(() => {
    if (!soilingRatioData || !Array.isArray(soilingRatioData)) {
      return [];
    }

    return soilingRatioData
      .filter((curve: any) => curve.name !== 'COMBINED' && (role === 'ex_post_analysis' ? curve.name !== 'hsu' : true)) // Exclude only the COMBINED curve
      .map((curve: any, index: number) => {
        // Get sensor name and format the curve label
        const sensorName = curve.file_uid ? getSensorName(curve.file_uid) : '';
        const curveName = curve.name || `Curve ${index + 1}`;
        const displayLabel = sensorName ? `${sensorName} - ${curveName}` : curveName;

        return {
          value: curveName, // Keep original curve name as value for API calls
          label: displayLabel, // Display name with sensor name
          file_uid: curve.file_uid,
          type: curve.type || 'rde300',
        };
      });
  }, [soilingRatioData, getSensorName]);

  // Load existing combined data and selected curves from formData on component mount
  useEffect(() => {
    // Only restore once and only when we have available curves
    if (hasRestoredDataRef.current || availableCurves.length === 0) {
      return;
    }

    const operationalData = formData?.[soilingRatioOperationalKey];

    if (operationalData?.combined_pattern) {
      // Restore combined chart data
      if (operationalData.combined_pattern.times && operationalData.combined_pattern.values) {
        setCombinedChartData({
          title: '',
          xTitle: 'Soiling Ratio',
          times: operationalData.combined_pattern.times,
          values: {
            combined_soiling_ratio: Array.isArray(operationalData.combined_pattern.values)
              ? operationalData.combined_pattern.values
              : operationalData.combined_pattern.values?.soiling_ratio || [],
          },
        });
      }

      // Restore selected curves based on sensor_files
      if (operationalData.sensor_files && Array.isArray(operationalData.sensor_files)) {
        const restoredCurves: any[] = [];

        operationalData.sensor_files.forEach((sensorFile: any) => {
          if (sensorFile.selected && Array.isArray(sensorFile.selected)) {
            sensorFile.selected.forEach((curveName: string) => {
              const matchingCurve = availableCurves.find((curve) => curve.file_uid === sensorFile.uid && curve.value === curveName);
              if (matchingCurve) {
                restoredCurves.push(matchingCurve);
              }
            });
          }
        });

        // Handle HSU selection persistence separately
        if (operationalData.hsu_selected) {
          const hsuCurve = availableCurves.find(
            (curve) => curve.value?.toLowerCase() === 'hsu' || curve.label?.toLowerCase().includes('hsu'),
          );
          if (hsuCurve && !restoredCurves.some((c) => c.value === hsuCurve.value && c.file_uid === hsuCurve.file_uid)) {
            restoredCurves.push(hsuCurve);
          }
        }

        if (restoredCurves.length > 0) {
          setSelectedCurves(restoredCurves);
        }
      }
    }

    // Mark as restored after processing (moved outside the conditional)
    hasRestoredDataRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCurves]);

  const handleCurveSelectionChange = (_event: any, newValue: any[]) => {
    setSelectedCurves(newValue);
  };

  const handleCombineCurves = async () => {
    if (selectedCurves.length === 0) {
      return;
    }

    setIsLoading(true);
    try {
      // Build body for /combine-soiling-ratios endpoint
      const getSensorFiles = () => {
        const sensorFiles: { uid: string; type: string; selected: string[]; name: string }[] = [];
        const soilingDataKeys = Object.keys(formData).filter(
          (key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'),
        );
        soilingDataKeys.forEach((blockKey) => {
          const fileUid = getFormDataItem(formData, `${blockKey}.file_uid`);
          const sensorName = getFormDataItem(formData, `${blockKey}.name`);
          const sensorType = getFormDataItem(formData, `${blockKey}.type`);
          if (fileUid) {
            // Filter selected curves that correspond to this file_uid
            const selectedCurvesForThisFile = selectedCurves.filter((curve) => curve.file_uid === fileUid).map((curve) => curve.value);

            sensorFiles.push({
              uid: fileUid,
              type: sensorType || 'rde300',
              selected: selectedCurvesForThisFile,
              name: sensorName || `Sensor ${fileUid.substring(0, 8)}...`, // Use the name or a fallback
            });
          }
        });
        return sensorFiles;
      };

      const sensorFiles = getSensorFiles();
      const hsuSelected = selectedCurves.some(
        (curve) => curve.value.toLowerCase().includes('hsu') || curve.label.toLowerCase().includes('hsu'),
      );

      // Validate that we have either sensor files or HSU selected
      if (sensorFiles.length === 0 && !hsuSelected) {
        throw new Error('No sensor files found. Please ensure you have uploaded soiling sensor data before combining curves.');
      }

      // Validate that at least one sensor file has selected curves OR HSU is selected
      const hasSelectedCurves = sensorFiles.some((file) => file.selected && file.selected.length > 0);
      if (!hasSelectedCurves && !hsuSelected) {
        throw new Error('No curves selected for combination. Please select at least one curve from the available sensors.');
      }

      const rainData = getFormDataItem(formData, 'rain2');
      const body = {
        hsu_selected: hsuSelected, // true if an HSU curve is selected
        sensor_files: sensorFiles,
        user_patterns: {
          rain_pattern_from_api: rainData?.from_api,
          rain_pattern: rainData?.from_api ? null : rainData?.rain_pattern,
        },
        plant: getFormDataItem(formData, 'plant'),
        use_plant_geometry: getFormDataItem(formData, 'use_plant_geometry'),
      };

      // Build parameters for /combine-soiling-ratios endpoint
      const combineParams = {
        project_id: params.id,
        longitude: getFormDataItem(formData, 'location.longitude'),
        latitude: getFormDataItem(formData, 'location.latitude'),
        compute_rain_efficiency: getFormDataItem(formData, 'rain.compute_rain_efficiency'),
        rain_efficiency: getFormDataItem(formData, 'rain.rain_efficiency'),
        rain_threshold: getFormDataItem(formData, 'rain_threshold'),
        variable_tilt_angle: getFormDataItem(formData, 'variable_tilt_angle'),
        surface_tilt: getFormDataItem(formData, 'surface_tilt'),
        tmy: role === 'operational_strategy' ? true : false, // Force TMY for operational strategy role
      };

      // Call combine-soiling-ratios endpoint
      const result = await postToServerWithParams(`${URL_BASE}combine-soiling-ratios`, combineParams, body);

      // Build local chart data for a dedicated chart (do not modify the shared SMT chart cache)
      if (result && result.times && result.values) {
        setCombinedChartData({
          title: '',
          xTitle: 'Soiling Ratio',
          times: result.times,
          values: {
            combined_soiling_ratio: Array.isArray(result.values) ? result.values : [],
          },
        });
      }

      // Update form data with the result and selection info
      const newFormData = {
        ...formData,
        [soilingRatioOperationalKey]: {
          combined_pattern: result,
          hsu_selected: body.hsu_selected,
          sensor_files: body.sensor_files,
        },
      };
      setFormData(newFormData);
      // Mark the form as modified so the save banner/button appears
      setModified(true);
    } catch (error) {
      console.error('Error combining curves:', error);
      // Display user-friendly error message
      const errorMessage = error instanceof Error ? error.message : 'An error occurred while combining curves. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isDataLoading || isDataFetching) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {isDataLoading ? 'Loading soiling ratio data...' : 'Refreshing soiling ratio data...'}
        </Typography>
      </Box>
    );
  }

  if (isDataError) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="error">
          Error loading soiling ratio data. Please check your configuration.
        </Typography>
      </Box>
    );
  }

  if (availableCurves.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No curves available. Please ensure:
          <br />• Location data is configured
          <br />• Soiling ratio data is loaded
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, width: '100%' }}>
      <Autocomplete
        multiple
        options={availableCurves}
        value={selectedCurves}
        onChange={handleCurveSelectionChange}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value && option.file_uid === value.file_uid}
        fullWidth
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Curves to Combine"
            placeholder="Choose curves..."
            helperText="Select multiple curves to combine them"
            fullWidth
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip {...getTagProps({ index })} key={option.value} label={option.label} color="primary" variant="outlined" />
          ))
        }
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          return (
            <Box component="li" key={key} {...otherProps}>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {option.label}
                </Typography>
              </Box>
            </Box>
          );
        }}
        sx={{ mb: 3, width: '50%' }}
      />

      <Button variant="contained" onClick={handleCombineCurves} disabled={selectedCurves.length === 0 || isLoading} sx={{ mt: 2 }}>
        {isLoading ? 'Combining...' : `Combine ${selectedCurves.length} Curve${selectedCurves.length !== 1 ? 's' : ''}`}
      </Button>

      {selectedCurves.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Please select at least one curve to combine.
        </Typography>
      )}

      {combinedChartData ? (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
            Combined Soiling Ratio
          </Typography>
          <Chart data={combinedChartData} ymin={0.4} ymax={1.2} />
        </Box>
      ) : null}
    </Box>
  );
};

export default SoilingRatioMultiSelect;
